import React, { useState, useEffect } from "react";
import useCommon from "hooks/useCommon";
import {
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { FaRegCalendarAlt, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import dayjs from "dayjs";

import VuiBox from "components/VuiBox";
import VuiButton from "components/VuiButton";
import VuiTypography from "components/VuiTypography";

import { FaPlaneArrival, FaPlaneDeparture } from "react-icons/fa";

import { getArrivalsCalendar } from "store/slice/arrivalsSlice";
import VuiTooltip from "components/VuiTooltip";

import colors from "assets/theme/base/colors";
import rgba from "assets/theme/functions/rgba";

const { primary, dark, light } = colors;

const StatBadge = ({ icon, label, value, color, bg }) => (
  <VuiBox
    display="flex"
    alignItems="center"
    gap={{ xs: 0.3, md: 0.5 }}
    px={{ xs: 0.35, md: 0.6 }}
    py={{ xs: 0.2, md: 0.4 }}
    my={{ xs: 0.15, md: 0.3 }}
    borderRadius="6px"
    sx={{
      border: `1px solid ${color}`,
      background: bg,
    }}
  >
    {icon}
    <VuiTypography
      variant="caption"
      sx={{
        fontSize: { xs: "0.55rem", md: "0.65rem" },
        lineHeight: 1,
        color,
        whiteSpace: "nowrap",
      }}
    >
      {label} {value}
    </VuiTypography>
  </VuiBox>
);

const CalendarDayCell = ({ day, arrivals, departures, onClick, isMobile }) => {
  const hasArrivals = arrivals > 0;
  const hasDepartures = departures > 0;

  return (
    <VuiBox
      p={{ xs: 0.5, sm: 0.8, md: 1 }}
      borderRadius="12px"
      onClick={onClick}
      sx={{
        border: `1px solid ${rgba(dark.main, 0.25)}`,
        background: light.secondary,
        minHeight: { xs: 38, sm: 48, md: 62 },
        cursor: "pointer",
      }}
    >
      <VuiTypography
        variant="button"
        fontWeight="regular"
        mb={hasArrivals || hasDepartures ? 0.3 : 0}
        sx={{
          lineHeight: 1,
          fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.85rem" },
        }}
      >
        {day}
      </VuiTypography>

      {(hasArrivals || hasDepartures) && (
        <VuiBox display="flex" flexDirection="column" gap={0.3}>
          {hasArrivals && (
            <StatBadge
              icon={<FaPlaneArrival color="rgba(0,200,140,0.9)" size={isMobile ? 7 : 9} />}
              label="Arrivals"
              value={arrivals}
              color="rgba(0,200,140,0.9)"
              bg="rgba(0,200,140,0.12)"
            />
          )}

          {hasDepartures && (
            <StatBadge
              icon={<FaPlaneDeparture color="rgba(255,170,60,0.95)" size={isMobile ? 7 : 9} />}
              label="Departs"
              value={departures}
              color="rgba(255,170,60,0.95)"
              bg="rgba(255,170,60,0.18)"
            />
          )}
        </VuiBox>
      )}
    </VuiBox>
  );
};

const MonthlyTotalsBar = ({ arrivals, departures }) => {
  return (
    <VuiBox
      display="flex"
      alignItems="center"
      justifyContent="center"
      gap={{ xs: 3, md: 6 }}
      my={1}
      px={2}
      borderRadius="14px"
    >
      {arrivals && (
        <VuiBox display="flex" alignItems="center" gap={1.2}>
          <VuiBox
            width={{ xs: 22, md: 28 }}
            height={{ xs: 22, md: 28 }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="8px"
            sx={{
              background: "rgba(0,200,140,0.15)",
            }}
          >
            <FaPlaneArrival size={12} color="rgba(0,200,140,0.95)" />
          </VuiBox>

          <VuiBox>
            <VuiTypography
              fontWeight="bold"
              sx={{
                lineHeight: 1,
                fontSize: { xs: "0.9rem", md: "1.2rem" },
              }}
            >
              {arrivals}
            </VuiTypography>

            <VuiTypography
              variant="caption"
              sx={{
                fontSize: { xs: "0.6rem", md: "0.75rem" },
              }}
            >
              Total Arrivals
            </VuiTypography>
          </VuiBox>
        </VuiBox>
      )}

      {departures && (
        <VuiBox display="flex" alignItems="center" gap={1.2}>
          <VuiBox
            width={{ xs: 22, md: 28 }}
            height={{ xs: 22, md: 28 }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="8px"
            sx={{
              background: "rgba(255,170,60,0.18)",
            }}
          >
            <FaPlaneDeparture size={12} color="rgba(255,170,60,0.95)" />
          </VuiBox>

          <VuiBox>
            <VuiTypography
              fontWeight="bold"
              sx={{
                lineHeight: 1,
                fontSize: { xs: "0.9rem", md: "1.2rem" },
              }}
            >
              {departures}
            </VuiTypography>

            <VuiTypography
              variant="caption"
              sx={{
                fontSize: { xs: "0.6rem", md: "0.75rem" },
              }}
            >
              Total Departures
            </VuiTypography>
          </VuiBox>
        </VuiBox>
      )}
    </VuiBox>
  );
};

const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const CalendarStatsButton = ({ setRange, setDateRange }) => {
  const { STORE, DISPATCH } = useCommon();
  const theme = useTheme();
  const { primary, dark, light } = colors;
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [open, setOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(dayjs());

  const hotelId = STORE((state) => state.ui.hotelId);
  const { data, loading } = STORE((state) => state.arrivals.calendar);

  const startOfMonth = currentMonth.startOf("month");
  const daysInMonth = currentMonth.daysInMonth();
  const startDay = startOfMonth.day();

  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const changeMonth = (dir) => {
    setCurrentMonth((prev) => (dir === "prev" ? prev.subtract(1, "month") : prev.add(1, "month")));
  };

  const handleDayClick = (day) => {
    const selected = startOfMonth.date(day);

    const startOfDay = selected.startOf("day").format("YYYY-MM-DD");
    const endOfDay = selected.endOf("day").format("YYYY-MM-DD");

    setDateRange([startOfDay, endOfDay]);
    setRange("all");

    setOpen(false);
  };

  useEffect(() => {
    if (!hotelId || !open) return;

    const fromUtc = currentMonth.startOf("month").toISOString();
    const toUtc = currentMonth.endOf("month").toISOString();

    DISPATCH(
      getArrivalsCalendar({
        hotelId,
        fromUtc,
        toUtc,
      })
    );
  }, [hotelId, currentMonth, open, DISPATCH]);

  return (
    <>
      <VuiTooltip title="Calendar View">
        <VuiButton
          variant="outlined"
          size="small"
          onClick={() => setOpen(true)}
          sx={{
            height: 38,
            padding: "0",
            color: primary.main,
            border: `1px solid ${rgba(dark.main, 1)}`,
            borderRadius: "14px",
            background: "transparent",
            "&:hover": {
              background: "rgba(255,255,255,0.04)",
              border: `1px solid ${rgba(dark.main, 0.5)}`,
            },
          }}
        >
          <FaRegCalendarAlt size={14} />
        </VuiButton>
      </VuiTooltip>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            width: { xs: "95vw", sm: "85vw", md: "800px" },
            maxWidth: "800px",
          },
        }}
      >
        <VuiBox display="flex" alignItems="center" justifyContent="space-between" p={2}>
          <IconButton onClick={() => changeMonth("prev")}>
            <FaChevronLeft size={14} />
          </IconButton>

          <VuiTypography variant="lg">{currentMonth.format("MMMM YYYY")}</VuiTypography>

          <IconButton onClick={() => changeMonth("next")}>
            <FaChevronRight size={14} />
          </IconButton>
        </VuiBox>

        <MonthlyTotalsBar arrivals={data?.totalArrivals} departures={data?.totalDepartures} />

        <DialogContent>
          <VuiBox position="relative">
            {loading && (
              <VuiBox
                position="absolute"
                top="50%"
                left="50%"
                sx={{
                  transform: "translate(-50%, -50%)",
                  zIndex: 10,
                }}
              >
                <CircularProgress size={28} sx={{ color: primary.main }} />
              </VuiBox>
            )}

            <VuiBox
              display="grid"
              gridTemplateColumns="repeat(7, 1fr)"
              gap={{ xs: 0.5, sm: 0.8, md: 1.2 }}
              sx={{
                opacity: loading ? 0.25 : 1,
                pointerEvents: loading ? "none" : "auto",
              }}
            >
              {/* WEEKDAYS */}
              {weekdays.map((day) => (
                <VuiTypography
                  key={day}
                  variant="caption"
                  textAlign="center"
                  opacity={0.6}
                  sx={{
                    fontSize: { xs: "0.55rem", md: "0.75rem" },
                    pb: 0.5,
                  }}
                >
                  {day}
                </VuiTypography>
              ))}

              {/* EMPTY CELLS */}
              {Array.from({ length: startDay }).map((_, idx) => (
                <VuiBox key={`empty-${idx}`} />
              ))}

              {/* DAYS */}
              {Array.from({ length: daysInMonth }).map((_, index) => {
                const day = index + 1;

                const dateKey = startOfMonth.date(day).format("YYYY-MM-DD");
                const { arrivals = 0, departures = 0 } = data?.days?.[dateKey] || {};

                return (
                  <CalendarDayCell
                    key={dateKey}
                    day={day}
                    arrivals={arrivals}
                    departures={departures}
                    isMobile={isMobile}
                    onClick={() => handleDayClick(day)}
                  />
                );
              })}
            </VuiBox>
          </VuiBox>
        </DialogContent>
        {/* <DialogContent>
          <VuiBox
            display="grid"
            gridTemplateColumns="repeat(7, 1fr)"
            gap={{ xs: 0.5, sm: 0.8, md: 1.2 }}
            mb={1}
          >
            {weekdays.map((day) => (
              <VuiTypography
                key={day}
                variant="caption"
                textAlign="center"
                
                opacity={0.6}
                sx={{
                  fontSize: { xs: "0.55rem", md: "0.75rem" },
                }}
              >
                {day}
              </VuiTypography>
            ))}
          </VuiBox>

          <VuiBox position="relative">
            {loading && (
              <VuiBox
                position="absolute"
                top="50%"
                left="50%"
                sx={{
                  transform: "translate(-50%, -50%)",
                  zIndex: 10,
                }}
              >
                <CircularProgress size={28} sx={{ color: "#1B2E24" }} />
              </VuiBox>
            )}

            <VuiBox
              display="grid"
              gridTemplateColumns="repeat(7, 1fr)"
              gap={{ xs: 0.5, sm: 0.8, md: 1.2 }}
              sx={{
                opacity: loading ? 0.25 : 1,
                pointerEvents: loading ? "none" : "auto",
              }}
            >
              {cells.map((day, idx) => {
                if (!day) return <VuiBox key={idx} />;

                const dateKey = startOfMonth.date(day).format("YYYY-MM-DD");
                const { arrivals = 0, departures = 0 } = data?.days?.[dateKey] || {};

                return (
                  <CalendarDayCell
                    key={dateKey}
                    day={day}
                    arrivals={arrivals}
                    departures={departures}
                    isMobile={isMobile}
                    onClick={() => handleDayClick(day)}
                  />
                );
              })}
            </VuiBox>
          </VuiBox>
        </DialogContent> */}
      </Dialog>
    </>
  );
};

export default CalendarStatsButton;
